// T2-BOARD-WAKE: the board settings sheet's "DE checks" tab — how often the board's owning DE
// (its lead seat, when a DE holds it) is woken with what changed, and the most wakes a day. Both
// live in the board's `settings` (`wake_window_minutes`, default 15; `wake_daily_cap`, default 48)
// and are written with `PATCH boards/{id}` by whoever may manage the board. Explanations sit
// behind (i) icons.
// Wave 20: the check window is the same `settings.wake_window_minutes` that "How it works"
// (T6-BOARD-NOTE) edits as "Checks every", and the owner shown here is the same
// `board.lead_seat_id`, through T6's owner picker. The form re-reads the board when its version
// moves, so a change made on the other tab shows here.
import { Button, InfoTip, Input, toast } from "@companyos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import { problemText } from "../../settings/problem-text";
import { invalidateWork } from "../work-queries";
import { OwnerPicker } from "./owner-picker";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

export const DEFAULT_WINDOW = 15;
export const DEFAULT_CAP = 48;

export function WakesTab(props: BoardSettingsTabProps) {
  return <WakesForm key={props.board.version} {...props} />;
}

function WakesForm({ board, can, api, co }: BoardSettingsTabProps) {
  const settings = (board.settings ?? {}) as Record<string, unknown>;
  const window0 =
    typeof settings.wake_window_minutes === "number"
      ? settings.wake_window_minutes
      : DEFAULT_WINDOW;
  const cap0 = typeof settings.wake_daily_cap === "number" ? settings.wake_daily_cap : DEFAULT_CAP;
  const [windowMin, setWindowMin] = useState(String(window0));
  const [cap, setCap] = useState(String(cap0));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const editable = can.manage && !board.archived_at && Boolean(api && co);
  const w = Number(windowMin);
  const c = Number(cap);
  const valid =
    Number.isInteger(w) && w >= 5 && w <= 1440 && Number.isInteger(c) && c >= 1 && c <= 500;
  const dirty = w !== window0 || c !== cap0;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!api || !co || !valid) return;
    setPending(true);
    setError(null);
    try {
      await api.client.boards.update({
        params: { co, id: board.id },
        ifMatch: board.version,
        body: { settings: { ...settings, wake_window_minutes: w, wake_daily_cap: c } },
      });
      toast.add({ title: "Saved", type: "success" });
      await invalidateWork(qc, co);
    } catch (err) {
      setError(problemText(err, "The settings could not be saved."));
    } finally {
      setPending(false);
    }
  };
  return (
    <form data-tab="wakes" className="flex flex-col gap-3 text-sm" onSubmit={save}>
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-4">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          Owner
          <InfoTip label="About DE checks">
            When a DE holds this board's owner (lead) seat, it is woken with what changed on the
            board: a person's comment, new card or move at once, everything else batched. Its own
            changes never wake it. The same owner as on "How it works".
          </InfoTip>
        </span>
        {api && co ? (
          <OwnerPicker api={api} co={co} board={board} editable={editable} />
        ) : (
          <span>{board.lead_seat_id ? "Owner seat set" : "No owner"}</span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-4">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          Check every (min)
          <InfoTip label="About the check window">
            Batched changes wait this long before the DE looks (5 to 1440 minutes). The same setting
            as "Checks every" on "How it works".
          </InfoTip>
        </span>
        <Input
          aria-label="Check every (minutes)"
          className="h-8 w-24 text-right tabular-nums"
          inputMode="numeric"
          disabled={!editable || pending}
          value={windowMin}
          onChange={(e) => setWindowMin(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-[1fr_auto] items-center gap-x-4">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          Wakes a day
          <InfoTip label="About the daily cap">
            The most times a day (UTC) the DE is woken for this board. Past it, only a person's
            comment, new card or move wakes it; the rest waits for the next day.
          </InfoTip>
        </span>
        <Input
          aria-label="Wakes a day at most"
          className="h-8 w-24 text-right tabular-nums"
          inputMode="numeric"
          disabled={!editable || pending}
          value={cap}
          onChange={(e) => setCap(e.target.value)}
        />
      </div>
      {editable ? (
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={pending || !valid || !dirty}>
            <CheckIcon aria-hidden="true" />
            Save
          </Button>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
    </form>
  );
}

const off = registerBoardSettingsTab({
  id: "wakes",
  title: "DE checks",
  order: 30,
  Component: WakesTab,
  when: (p) => Boolean(p.api && p.co),
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
